import styled, { css } from 'styled-components';

import { Icon, IconPropType } from '@/ui/Icon';
import {
  LiveEventSessionCardMeta,
  DateStyled,
  FormatStyled,
} from '@/pages/LiveEvent/LiveEventSessionCardMeta/LiveEventSessionCardMeta';
import { RLocationType } from '@/store/v2';

type LiveEventSessionCardVariant = 'default' | 'selected' | 'checked' | 'disabled';

const variantToRadioIcon: Record<LiveEventSessionCardVariant, IconPropType> = {
  default: ['far', 'circle'],
  selected: ['far', 'circle-dot'],
  checked: ['far', 'circle-check'],
  disabled: ['far', 'circle'],
};

const RootStyled = styled.div<{ variant: LiveEventSessionCardVariant }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid ${({ theme }) => theme.colors.neutral.outline1};
  border-radius: 8px;
  padding: 16px;
  box-sizing: border-box;
  transition: background-color 0.3s;
  cursor: pointer;

  ${({ variant }) => {
    switch (variant) {
      case 'default':
        return css`
          &:hover {
            border: 1px solid ${({ theme }) => theme.colors.neutral.onBackground};
            background-color: ${({ theme }) => theme.colors.states.hover5};
          }
        `;
      case 'selected':
        return css`
          border: 2px solid ${({ theme }) => theme.colors.tertiary.outline};
          background-color: ${({ theme }) => theme.tones.tertiary[95]};
          padding: 15px;
        `;
      case 'checked':
        return css`
          border: 2px solid ${({ theme }) => theme.colors.primary.outline};
          background-color: ${({ theme }) => theme.tones.primary[99]};
          padding: 15px;
        `;
      case 'disabled':
        return css`
          border: 1px solid ${({ theme }) => theme.colors.neutral.disabled};
          color: ${({ theme }) => theme.colors.neutral.disabled};
          cursor: not-allowed;

          ${NameStyled} {
            color: ${({ theme }) => theme.colors.neutral.disabled};
          }

          ${RadioButtonIconStyled} {
            color: ${({ theme }) => theme.colors.neutral.disabled};
          }

          ${DateStyled} {
            color: ${({ theme }) => theme.colors.neutral.disabled};
          }

          ${FormatStyled} {
            color: ${({ theme }) => theme.colors.neutral.disabled};
          }

          svg {
            color: ${({ theme }) => theme.colors.neutral.disabled};
          }
        `;
    }
  }}
`;

const NameStyled = styled.div`
  ${({ theme }) => theme.typography.body2};
  color: ${({ theme }) => theme.colors.neutral.onBackground};
`;

const HeadingStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RadioButtonIconStyled = styled(Icon)`
  width: 14px;
  height: 14px;
  color: ${({ theme }) => theme.colors.neutral.onBackground};
`;

const BadgeLabelStyled = styled.div`
  background-color: ${({ theme }) => theme.colors.primary.main};
  color: ${({ theme }) => theme.colors.primary.onMain};
  padding: 0px 4px;
  border-radius: 4px;
  ${({ theme }) => theme.typography.caption};
`;

const LiveEventSessionCardMetaContainerStyled = styled.div`
  padding-left: 22px;
`;

type LiveEventSessionCardProps = {
  name: string;
  date: string;
  type: RLocationType;
  variant?: LiveEventSessionCardVariant;
  className?: string;
  onClick?: () => void;
};

export const LiveEventSessionCard = ({
  name,
  date,
  type,
  variant = 'default',
  className,
  onClick,
}: LiveEventSessionCardProps): JSX.Element | null => {
  return (
    <RootStyled className={className} variant={variant} onClick={variant !== 'disabled' ? onClick : undefined}>
      <HeadingStyled>
        <RadioButtonIconStyled icon={variantToRadioIcon[variant]} />
        <NameStyled>{name}</NameStyled>
        {variant === 'checked' && <BadgeLabelStyled>Reserved</BadgeLabelStyled>}
      </HeadingStyled>
      <LiveEventSessionCardMetaContainerStyled>
        <LiveEventSessionCardMeta date={date} type={type} />
      </LiveEventSessionCardMetaContainerStyled>
    </RootStyled>
  );
};
